import React, { useState, useEffect, useRef } from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX, DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    AppBar,
    Toolbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Tooltip,
    TableSortLabel,
    Button,
    Switch,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    People,
    Business as BusinessIcon,
    CurrencyRupee,
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Phone as PhoneIcon,
    Storage as StorageIcon,
    PersonAdd as PersonAddIcon,
    PersonRemove as PersonRemoveIcon,
    WhatsApp as WhatsAppIcon,
    LocationCity as LocationCityIcon,
    Add as AddIcon,
    EditOutlined as EditIcon,
    DeleteOutline as DeleteIcon,
    Inventory2 as InventoryIcon,
    PhotoCamera as PhotoCameraIcon,
    DeleteForeverOutlined as DeleteBusinessIcon,
    RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Autocomplete from '@mui/material/Autocomplete';
import { adminAPI } from '../api';

const PROMO_TEXT = `Hey! I use *Vendora88* to run my business — it handles orders, billing, customer dues, and monthly subscriptions all in one place. It's really simple to use!\n\nCheck it out: https://vendora88.com\n\nWorks great for:\n🍽️ Restaurants & food orders\n👕 Ironing & laundry services\n🥛 Milk & water billing\n📰 Newspaper billing\n📡 Cable & internet operators billing\n...and any business that needs daily orders or monthly billing.\n\nTry it free!`;

const waLink = (mobile, withText = false) => {
    const digits = (mobile || '').replace(/\D/g, '');
    const phone = digits.length === 10 ? `91${digits}` : digits;
    const base = `https://wa.me/${phone}`;
    return withText ? `${base}?text=${encodeURIComponent(PROMO_TEXT)}` : base;
};

const KpiCard = ({ title, value, icon, color = 'primary.main' }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Box>
                    <Typography variant="caption" color="text.secondary" noWrap>{title}</Typography>
                    <Typography variant="h6" fontWeight="bold">{value}</Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const SuperAdminDashboard = ({ onBack }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [usersOpen, setUsersOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [tablesOpen, setTablesOpen] = useState(false);
    const [tables, setTables] = useState([]);
    const [tablesLoading, setTablesLoading] = useState(false);
    const [joiningId, setJoiningId] = useState(null);
    const [joinedIds, setJoinedIds] = useState(new Set());
    const [leavingId, setLeavingId] = useState(null);
    const [leftIds, setLeftIds] = useState(new Set());
    const [togglingStoreId, setTogglingStoreId] = useState(null);
    const [storeEnabledOverrides, setStoreEnabledOverrides] = useState({});
    const [showDeleted, setShowDeleted] = useState(false);
    const [busyBizId, setBusyBizId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [communitiesOpen, setCommunitiesOpen] = useState(false);
    const [masterProductsOpen, setMasterProductsOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

    const fetchDashboard = async (deleted = showDeleted) => {
        setLoading(true);
        setError('');
        try {
            const res = await adminAPI.getDashboard(deleted);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load admin dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleShowDeletedChange = (deleted) => {
        if (deleted === null || deleted === showDeleted) return;
        setShowDeleted(deleted);
        setSearch('');
        fetchDashboard(deleted);
    };

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const res = await adminAPI.getUsers();
            setUsers(res.data.users);
        } catch (err) {
            console.error('Failed to load users', err);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleUsersCardClick = () => {
        setUsersOpen(true);
        setUserSearch('');
        fetchUsers();
    };

    const fetchTableSizes = async () => {
        setTablesLoading(true);
        try {
            const res = await adminAPI.getTableSizes();
            setTables(res.data.tables);
        } catch (err) {
            console.error('Failed to load table sizes', err);
        } finally {
            setTablesLoading(false);
        }
    };

    const handleTablesCardClick = () => {
        setTablesOpen(true);
        fetchTableSizes();
    };

    const handleJoinBusiness = async (businessId, businessName) => {
        setJoiningId(businessId);
        try {
            await adminAPI.joinBusiness(businessId);
            setJoinedIds((prev) => new Set([...prev, businessId]));
            setLeftIds((prev) => { const s = new Set(prev); s.delete(businessId); return s; });
            setSnack({ open: true, message: `Joined "${businessName}" as Admin`, severity: 'success' });
        } catch (err) {
            setSnack({ open: true, message: err.response?.data?.detail || 'Failed to join business', severity: 'error' });
        } finally {
            setJoiningId(null);
        }
    };

    const handleLeaveBusiness = async (businessId, businessName) => {
        setLeavingId(businessId);
        try {
            await adminAPI.leaveBusiness(businessId);
            setLeftIds((prev) => new Set([...prev, businessId]));
            setJoinedIds((prev) => { const s = new Set(prev); s.delete(businessId); return s; });
            setSnack({ open: true, message: `Left "${businessName}"`, severity: 'info' });
        } catch (err) {
            setSnack({ open: true, message: err.response?.data?.detail || 'Failed to leave business', severity: 'error' });
        } finally {
            setLeavingId(null);
        }
    };

    const handleTogglePublicStore = async (businessId) => {
        setTogglingStoreId(businessId);
        try {
            const res = await adminAPI.togglePublicStore(businessId);
            setStoreEnabledOverrides(prev => ({ ...prev, [businessId]: res.data.f_public_store_enabled }));
            setSnack({ open: true, message: `Public store ${res.data.f_public_store_enabled ? 'enabled' : 'disabled'}`, severity: 'success' });
        } catch {
            setSnack({ open: true, message: 'Failed to update', severity: 'error' });
        } finally {
            setTogglingStoreId(null);
        }
    };

    const handleDeleteBusiness = async () => {
        const target = confirmDelete;
        if (!target) return;
        setBusyBizId(target.business_id);
        try {
            await adminAPI.deleteBusiness(target.business_id);
            setConfirmDelete(null);
            setData((d) => ({
                ...d,
                summary: {
                    ...d.summary,
                    total_businesses: Math.max(0, (d.summary.total_businesses || 0) - 1),
                    deleted_businesses: (d.summary.deleted_businesses || 0) + 1,
                },
                vendors: d.vendors.filter((v) => v.business_id !== target.business_id),
            }));
            setSnack({ open: true, message: `Deleted "${target.business_name}" — you can restore it any time`, severity: 'success' });
        } catch (err) {
            setSnack({ open: true, message: err.response?.data?.detail || 'Failed to delete business', severity: 'error' });
        } finally {
            setBusyBizId(null);
        }
    };

    const handleRestoreBusiness = async (businessId, businessName) => {
        setBusyBizId(businessId);
        try {
            await adminAPI.restoreBusiness(businessId);
            setData((d) => ({
                ...d,
                summary: {
                    ...d.summary,
                    total_businesses: (d.summary.total_businesses || 0) + 1,
                    deleted_businesses: Math.max(0, (d.summary.deleted_businesses || 0) - 1),
                },
                vendors: d.vendors.filter((v) => v.business_id !== businessId),
            }));
            setSnack({ open: true, message: `Restored "${businessName}"`, severity: 'success' });
        } catch (err) {
            setSnack({ open: true, message: err.response?.data?.detail || 'Failed to restore business', severity: 'error' });
        } finally {
            setBusyBizId(null);
        }
    };

    useEffect(() => { fetchDashboard(false); }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const { summary, vendors } = data;

    const filtered = vendors.filter((v) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (v.business_name || '').toLowerCase().includes(q) ||
            (v.owner_name || '').toLowerCase().includes(q) ||
            (v.owner_mobile || '').includes(q) ||
            (v.city || '').toLowerCase().includes(q)
        );
    });

    const fmt = (n) => {
        if (n == null) return '—';
        return new Intl.NumberFormat('en-IN').format(n);
    };

    const fmtCurrency = (n) => {
        if (n == null) return '—';
        return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
    };

    return (
        <>
            {/* Top bar */}
            <AppBar position="fixed" color="default" elevation={1} sx={{ top: 0, paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                <Toolbar variant="dense" sx={{ minHeight: 48 }}>
                    <IconButton edge="start" onClick={onBack} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        Platform Admin
                    </Typography>
                    <IconButton onClick={() => fetchDashboard()} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 2, pb: 4, mt: 'calc(48px + env(safe-area-inset-top, 0px))' }}>
                {/* KPI cards */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6} onClick={handleUsersCardClick} sx={{ cursor: 'pointer' }}>
                        <KpiCard title="Users" value={fmt(summary.total_users)} icon={<People fontSize="small" />} />
                    </Grid>
                    <Grid item xs={6}>
                        <KpiCard title="Businesses" value={fmt(summary.total_businesses)} icon={<BusinessIcon fontSize="small" />} color="success.main" />
                    </Grid>
                    <Grid item xs={6}>
                        <KpiCard title="Customers" value={fmt(summary.total_customers)} icon={<People fontSize="small" />} color="info.main" />
                    </Grid>
                    <Grid item xs={6}>
                        <KpiCard title="Total Payments" value={fmtCurrency(summary.total_payments)} icon={<CurrencyRupee fontSize="small" />} color="warning.main" />
                    </Grid>
                    <Grid item xs={6} onClick={handleTablesCardClick} sx={{ cursor: 'pointer' }}>
                        <KpiCard title="Table Sizes" value="View" icon={<StorageIcon fontSize="small" />} color="secondary.main" />
                    </Grid>
                    <Grid item xs={6} onClick={() => setCommunitiesOpen(true)} sx={{ cursor: 'pointer' }}>
                        <KpiCard title="Communities" value="Manage" icon={<LocationCityIcon fontSize="small" />} color="info.main" />
                    </Grid>
                    <Grid item xs={6} onClick={() => setMasterProductsOpen(true)} sx={{ cursor: 'pointer' }}>
                        <KpiCard title="Master Products" value="Manage" icon={<InventoryIcon fontSize="small" />} color="primary.main" />
                    </Grid>
                </Grid>

                {/* Active / Deleted switch */}
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={showDeleted}
                    onChange={(_, v) => handleShowDeletedChange(v)}
                    sx={{ mb: 1.5 }}
                >
                    <ToggleButton value={false} sx={{ textTransform: 'none', px: 2 }}>
                        Active ({fmt(summary.total_businesses)})
                    </ToggleButton>
                    <ToggleButton value={true} sx={{ textTransform: 'none', px: 2 }}>
                        Deleted ({fmt(summary.deleted_businesses)})
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Search */}
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search vendor, owner, mobile, city..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                        ),
                    }}
                />

                {/* Vendor table */}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {filtered.length} {showDeleted ? 'Deleted ' : ''}Vendor{filtered.length !== 1 ? 's' : ''}
                </Typography>

                <TableContainer component={Paper} elevation={1} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Business</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Owner</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>City</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Cust</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Prod</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Members</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Period</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Locked</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Invoiced</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Paid</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Due</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Last Pay</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Registered</TableCell>
                                {!showDeleted && <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">Store</TableCell>}
                                {!showDeleted && <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">Join</TableCell>}
                                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">
                                    {showDeleted ? 'Restore' : 'Delete'}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((v) => (
                                <TableRow key={v.business_id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.75rem' }}>{v.business_id}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.business_name}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Box>
                                            <Typography variant="body2">{v.owner_name || '—'}</Typography>
                                            <Box
                                                component="a"
                                                href={waLink(v.owner_mobile, true)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: '#25D366', textDecoration: 'none' }}
                                            >
                                                <WhatsAppIcon sx={{ fontSize: 13 }} />
                                                <Typography variant="caption">{v.owner_mobile}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{v.city || '—'}</TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`${v.active_customers}/${v.total_customers}`}
                                            size="small"
                                            color={v.active_customers > 0 ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{v.products}</TableCell>
                                    <TableCell align="right">{v.members}</TableCell>
                                    <TableCell>
                                        {v.working_period ? (
                                            <Chip label={v.working_period} size="small" variant="outlined" />
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell align="right">{v.locked_periods}</TableCell>
                                    <TableCell align="right">{fmtCurrency(v.total_invoiced)}</TableCell>
                                    <TableCell align="right">{fmtCurrency(v.total_paid)}</TableCell>
                                    <TableCell align="right">
                                        <Typography
                                            variant="body2"
                                            color={v.outstanding > 0 ? 'error.main' : 'success.main'}
                                            fontWeight="bold"
                                        >
                                            {fmtCurrency(v.outstanding)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {v.last_payment ? new Date(v.last_payment).toLocaleDateString('en-IN') : '—'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {v.registered ? new Date(v.registered).toLocaleDateString('en-IN') : '—'}
                                    </TableCell>
                                    {!showDeleted && <TableCell align="center">
                                        <Tooltip title={(storeEnabledOverrides[v.business_id] ?? v.public_store_enabled) ? 'Disable public store' : 'Enable public store'}>
                                            <span>
                                                <Switch
                                                    size="small"
                                                    checked={storeEnabledOverrides[v.business_id] ?? v.public_store_enabled ?? false}
                                                    onChange={() => handleTogglePublicStore(v.business_id)}
                                                    disabled={togglingStoreId === v.business_id}
                                                    color="primary"
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>}
                                    {!showDeleted && <TableCell align="center">
                                        {(() => {
                                            const isMember = (v.is_admin_member || joinedIds.has(v.business_id)) && !leftIds.has(v.business_id);
                                            if (isMember) {
                                                return (
                                                    <Tooltip title="Leave business">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                disabled={leavingId === v.business_id}
                                                                onClick={() => handleLeaveBusiness(v.business_id, v.business_name)}
                                                            >
                                                                {leavingId === v.business_id
                                                                    ? <CircularProgress size={16} />
                                                                    : <PersonRemoveIcon fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                );
                                            }
                                            return (
                                                <Tooltip title="Join as Admin">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={joiningId === v.business_id}
                                                            onClick={() => handleJoinBusiness(v.business_id, v.business_name)}
                                                        >
                                                            {joiningId === v.business_id
                                                                ? <CircularProgress size={16} />
                                                                : <PersonAddIcon fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            );
                                        })()}
                                    </TableCell>}
                                    <TableCell align="center">
                                        {showDeleted ? (
                                            <Tooltip title="Restore this business">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        disabled={busyBizId === v.business_id}
                                                        onClick={() => handleRestoreBusiness(v.business_id, v.business_name)}
                                                    >
                                                        {busyBizId === v.business_id
                                                            ? <CircularProgress size={16} />
                                                            : <RestoreIcon fontSize="small" />
                                                        }
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Delete this business">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={busyBizId === v.business_id}
                                                        onClick={() => setConfirmDelete(v)}
                                                    >
                                                        {busyBizId === v.business_id
                                                            ? <CircularProgress size={16} />
                                                            : <DeleteBusinessIcon fontSize="small" />
                                                        }
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={showDeleted ? 15 : 17} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {showDeleted ? 'No deleted vendors' : 'No vendors found'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <UserListDialog
                open={usersOpen}
                onClose={() => setUsersOpen(false)}
                users={users}
                loading={usersLoading}
                search={userSearch}
                onSearchChange={setUserSearch}
            />

            <TableSizesDialog
                open={tablesOpen}
                onClose={() => setTablesOpen(false)}
                tables={tables}
                loading={tablesLoading}
            />

            <CommunitiesDialog
                open={communitiesOpen}
                onClose={() => setCommunitiesOpen(false)}
                onSnack={(msg, sev) => setSnack({ open: true, message: msg, severity: sev || 'success' })}
            />

            <MasterProductsDialog
                open={masterProductsOpen}
                onClose={() => setMasterProductsOpen(false)}
                onSnack={(msg, sev) => setSnack({ open: true, message: msg, severity: sev || 'success' })}
            />

            <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 1 }}>Delete this business?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        <strong>{confirmDelete?.business_name}</strong> ({confirmDelete?.owner_name || 'no owner name'}) will be
                        hidden from the owner, staff and customers.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Nothing is erased — you can bring it back any time from the Deleted list.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteBusiness}
                        disabled={busyBizId === confirmDelete?.business_id}
                        sx={{ textTransform: 'none' }}
                    >
                        {busyBizId === confirmDelete?.business_id ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    );
};

const UserListDialog = ({ open, onClose, users, loading, search, onSearchChange }) => {
    const [sortDir, setSortDir] = useState('desc');

    const filtered = users
        .filter((u) => {
            if (!search) return true;
            const q = search.toLowerCase();
            return (
                (u.name || '').toLowerCase().includes(q) ||
                (u.mobile || '').includes(q)
            );
        })
        .sort((a, b) => {
            const ta = a.last_access ? new Date(a.last_access).getTime() : 0;
            const tb = b.last_access ? new Date(b.last_access).getTime() : 0;
            return sortDir === 'desc' ? tb - ta : ta - tb;
        });

    const fmtDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const fmtDateTime = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    Users ({filtered.length})
                </Typography>
                <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 1, pt: 0 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search name or mobile..."
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    sx={{ mb: 1, px: 1 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                />
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={1}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Mobile</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">Biz</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Registered</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                        <TableSortLabel
                                            active
                                            direction={sortDir}
                                            onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                        >
                                            Last Active
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((u) => (
                                    <TableRow key={u.id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {u.name || '(No name)'}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                <Box component="a" href={`tel:${u.mobile}`} sx={{ color: 'primary.main', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                                    <PhoneIcon sx={{ fontSize: 13 }} />
                                                    <Typography variant="caption">{u.mobile}</Typography>
                                                </Box>
                                                <Box
                                                    component="a"
                                                    href={waLink(u.mobile, true)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ color: '#25D366', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
                                                >
                                                    <WhatsAppIcon sx={{ fontSize: 13 }} />
                                                    <Typography variant="caption">Share Vendora</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{u.businesses}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(u.registered)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(u.last_access)}</TableCell>
                                    </TableRow>
                                ))}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No users found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

const TableSizesDialog = ({ open, onClose, tables, loading }) => {
    const totalRows = tables.reduce((s, t) => s + t.rows, 0);
    const totalDeleted = tables.reduce((s, t) => s + t.deleted, 0);

    return (
        <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    Table Sizes ({tables.length})
                </Typography>
                <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 1, pt: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ px: 1, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total: {totalRows.toLocaleString('en-IN')} rows &nbsp;|&nbsp; Deleted: {totalDeleted.toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                        <TableContainer component={Paper} elevation={1}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Table</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Rows</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Deleted</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tables.map((t) => (
                                        <TableRow key={t.table} hover>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {t.table.replace('t_', '')}
                                            </TableCell>
                                            <TableCell align="right">{t.rows.toLocaleString('en-IN')}</TableCell>
                                            <TableCell align="right" sx={{ color: t.deleted > 0 ? 'error.main' : 'text.secondary' }}>
                                                {t.deleted > 0 ? t.deleted.toLocaleString('en-IN') : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

const EMPTY_FORM = { f_name: '', f_city: '', f_state: '', f_pincode: '', f_latitude: '', f_longitude: '', f_google_place_id: '' };

const BILLING_OPTIONS = [
    { value: 'PerEachItem', label: 'Per Item' },
    { value: 'MonthlyFlatFee', label: 'Monthly' },
];
const EMPTY_PRODUCT_FORM = {
    f_category_id: '', f_name: '', f_description: '', f_billing_type: 'PerEachItem',
    f_base_price: '', f_min_order_qty: '1', f_increment_order_qty: '1', f_is_active: true,
};
const EMPTY_CATEGORY_FORM = { f_name: '', f_description: '', f_is_active: true };

const MasterProductsDialog = ({ open, onClose, onSnack }) => {
    const [tab, setTab] = useState(0);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [catFilter, setCatFilter] = useState('');
    const [search, setSearch] = useState('');

    // Product form
    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [form, setForm] = useState(EMPTY_PRODUCT_FORM);
    const [saving, setSaving] = useState(false);
    const [imgBusy, setImgBusy] = useState(false);

    // Category form
    const [catFormOpen, setCatFormOpen] = useState(false);
    const [catEditTarget, setCatEditTarget] = useState(null);
    const [catForm, setCatForm] = useState(EMPTY_CATEGORY_FORM);
    const [catSaving, setCatSaving] = useState(false);

    const catName = (id) => categories.find(c => c.f_id === id)?.f_name || '—';

    const fetchCategories = async () => {
        try {
            const res = await adminAPI.getProductCategories();
            setCategories(res.data.categories);
        } catch {
            onSnack('Failed to load categories', 'error');
        }
    };

    const fetchProducts = async (categoryId = catFilter) => {
        setLoading(true);
        try {
            const res = await adminAPI.getMasterProducts(categoryId || null);
            setProducts(res.data.products);
        } catch {
            onSnack('Failed to load products', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        fetchCategories();
        fetchProducts('');
        setCatFilter('');
        setSearch('');
        setTab(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // ── Product CRUD ──
    const openCreateProduct = () => {
        setEditTarget(null);
        setForm({ ...EMPTY_PRODUCT_FORM, f_category_id: catFilter || (categories[0]?.f_id ?? '') });
        setFormOpen(true);
    };
    const openEditProduct = (p) => {
        setEditTarget(p);
        setForm({
            f_category_id: p.f_category_id,
            f_name: p.f_name || '',
            f_description: p.f_description || '',
            f_billing_type: p.f_billing_type,
            f_base_price: p.f_base_price != null ? String(p.f_base_price) : '',
            f_min_order_qty: p.f_min_order_qty != null ? String(p.f_min_order_qty) : '1',
            f_increment_order_qty: p.f_increment_order_qty != null ? String(p.f_increment_order_qty) : '1',
            f_is_active: p.f_is_active,
        });
        setFormOpen(true);
    };
    const saveProduct = async () => {
        if (!form.f_name.trim() || !form.f_category_id) { onSnack('Name and category are required', 'error'); return; }
        const payload = {
            f_category_id: parseInt(form.f_category_id),
            f_name: form.f_name.trim(),
            f_description: form.f_description.trim() || null,
            f_billing_type: form.f_billing_type,
            f_base_price: form.f_base_price ? parseFloat(form.f_base_price) : null,
            f_min_order_qty: form.f_min_order_qty ? parseFloat(form.f_min_order_qty) : 1,
            f_increment_order_qty: form.f_increment_order_qty ? parseFloat(form.f_increment_order_qty) : 1,
        };
        setSaving(true);
        try {
            if (editTarget) {
                const res = await adminAPI.updateMasterProduct(editTarget.f_id, { ...payload, f_is_active: form.f_is_active });
                setProducts(prev => prev.map(x => x.f_id === editTarget.f_id ? res.data : x));
                setEditTarget(res.data);
                onSnack('Product updated');
            } else {
                const res = await adminAPI.createMasterProduct(payload);
                setProducts(prev => [...prev, res.data]);
                setEditTarget(res.data);  // keep dialog open so admin can add images
                onSnack('Product created — you can now add photos');
            }
            fetchCategories();  // refresh counts
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Failed to save product', 'error');
        } finally {
            setSaving(false);
        }
    };
    const deleteProduct = async (p) => {
        if (!window.confirm(`Delete "${p.f_name}"? This also removes its photos.`)) return;
        try {
            await adminAPI.deleteMasterProduct(p.f_id);
            setProducts(prev => prev.filter(x => x.f_id !== p.f_id));
            fetchCategories();
            onSnack('Product deleted');
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Failed to delete product', 'error');
        }
    };

    // ── Images ──
    const handleUploadImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !editTarget) return;
        setImgBusy(true);
        try {
            const res = await adminAPI.uploadMasterProductImage(editTarget.f_id, file);
            const updated = { ...editTarget, images: res.data.images, f_image_folder: res.data.folder };
            setEditTarget(updated);
            setProducts(prev => prev.map(x => x.f_id === editTarget.f_id ? updated : x));
            onSnack('Photo added');
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Upload failed', 'error');
        } finally {
            setImgBusy(false);
        }
    };
    const handleDeleteImage = async (url) => {
        if (!editTarget) return;
        const marker = '.amazonaws.com/';
        const key = url.split(marker)[1];
        setImgBusy(true);
        try {
            const res = await adminAPI.deleteMasterProductImage(editTarget.f_id, key);
            const updated = { ...editTarget, images: res.data.images };
            setEditTarget(updated);
            setProducts(prev => prev.map(x => x.f_id === editTarget.f_id ? updated : x));
            onSnack('Photo removed');
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Delete failed', 'error');
        } finally {
            setImgBusy(false);
        }
    };

    // ── Category CRUD ──
    const openCreateCategory = () => { setCatEditTarget(null); setCatForm(EMPTY_CATEGORY_FORM); setCatFormOpen(true); };
    const openEditCategory = (c) => {
        setCatEditTarget(c);
        setCatForm({ f_name: c.f_name || '', f_description: c.f_description || '', f_is_active: c.f_is_active });
        setCatFormOpen(true);
    };
    const saveCategory = async () => {
        if (!catForm.f_name.trim()) { onSnack('Category name is required', 'error'); return; }
        setCatSaving(true);
        try {
            if (catEditTarget) {
                await adminAPI.updateProductCategory(catEditTarget.f_id, {
                    f_name: catForm.f_name.trim(), f_description: catForm.f_description.trim() || null, f_is_active: catForm.f_is_active,
                });
                onSnack('Category updated');
            } else {
                await adminAPI.createProductCategory({ f_name: catForm.f_name.trim(), f_description: catForm.f_description.trim() || null });
                onSnack('Category created');
            }
            setCatFormOpen(false);
            fetchCategories();
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Failed to save category', 'error');
        } finally {
            setCatSaving(false);
        }
    };
    const deleteCategory = async (c) => {
        if (!window.confirm(`Delete category "${c.f_name}"?`)) return;
        try {
            await adminAPI.deleteProductCategory(c.f_id);
            onSnack('Category deleted');
            fetchCategories();
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Failed to delete category', 'error');
        }
    };

    const visibleProducts = products.filter(p =>
        !search.trim() || p.f_name?.toLowerCase().includes(search.trim().toLowerCase())
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper"
                PaperProps={{ sx: { borderRadius: 2, height: '90vh' } }}>
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Master Products
                    <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={`Products (${products.length})`} />
                    <Tab label={`Categories (${categories.length})`} />
                </Tabs>
                <DialogContent dividers sx={{ p: 1.5 }}>
                    {/* ── Tab 0: Products ── */}
                    {tab === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                <TextField size="small" fullWidth placeholder="Search products…"
                                    value={search} onChange={e => setSearch(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                                />
                                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreateProduct}
                                    sx={{ flexShrink: 0, textTransform: 'none' }}>Add</Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                                <Chip size="small" label="All" color={catFilter === '' ? 'primary' : 'default'}
                                    onClick={() => { setCatFilter(''); fetchProducts(''); }} clickable />
                                {categories.map(c => (
                                    <Chip key={c.f_id} size="small" label={c.f_name}
                                        color={catFilter === c.f_id ? 'primary' : 'default'}
                                        onClick={() => { setCatFilter(c.f_id); fetchProducts(c.f_id); }} clickable />
                                ))}
                            </Box>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                            ) : visibleProducts.length === 0 ? (
                                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No products</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {visibleProducts.map(p => (
                                        <Paper key={p.f_id} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 2, opacity: p.f_is_active ? 1 : 0.55 }}>
                                            <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: 'grey.100', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {p.images?.[0]
                                                    ? <Box component="img" src={p.images[0]} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    : <PhotoCameraIcon sx={{ color: 'text.disabled', fontSize: 20 }} />}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>{p.f_name}</Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.25 }}>
                                                    <Chip size="small" label={catName(p.f_category_id)} sx={{ height: 18, fontSize: '0.65rem' }} />
                                                    {p.f_base_price != null && <Typography variant="caption" color="text.secondary">₹{p.f_base_price}</Typography>}
                                                    {p.images?.length > 0 && <Typography variant="caption" color="text.secondary">· {p.images.length} 📷</Typography>}
                                                </Box>
                                            </Box>
                                            <IconButton size="small" onClick={() => openEditProduct(p)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => deleteProduct(p)}><DeleteIcon fontSize="small" /></IconButton>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* ── Tab 1: Categories ── */}
                    {tab === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreateCategory}
                                    sx={{ textTransform: 'none' }}>Add Category</Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {categories.map(c => (
                                    <Paper key={c.f_id} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, opacity: c.f_is_active ? 1 : 0.55 }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>{c.f_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{c.product_count} product(s){!c.f_is_active && ' · inactive'}</Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => openEditCategory(c)}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => deleteCategory(c)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Paper>
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Product form dialog ── */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs" scroll="paper">
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {editTarget ? 'Edit Product' : 'New Product'}
                    <IconButton size="small" onClick={() => setFormOpen(false)}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
                    <TextField select size="small" label="Category" value={form.f_category_id}
                        onChange={e => setForm(f => ({ ...f, f_category_id: e.target.value }))}>
                        {categories.map(c => <MenuItem key={c.f_id} value={c.f_id}>{c.f_name}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Name" value={form.f_name}
                        onChange={e => setForm(f => ({ ...f, f_name: e.target.value }))} />
                    <TextField size="small" label="Description" multiline minRows={2} value={form.f_description}
                        onChange={e => setForm(f => ({ ...f, f_description: e.target.value }))} />
                    <TextField select size="small" label="Billing" value={form.f_billing_type}
                        onChange={e => setForm(f => ({ ...f, f_billing_type: e.target.value }))}>
                        {BILLING_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField size="small" label="Base Price" type="number" value={form.f_base_price}
                            onChange={e => setForm(f => ({ ...f, f_base_price: e.target.value }))}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField size="small" label="Min Order Qty" type="number" value={form.f_min_order_qty}
                            onChange={e => setForm(f => ({ ...f, f_min_order_qty: e.target.value }))} />
                        <TextField size="small" label="Increment Qty" type="number" value={form.f_increment_order_qty}
                            onChange={e => setForm(f => ({ ...f, f_increment_order_qty: e.target.value }))} />
                    </Box>
                    {editTarget && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch size="small" checked={form.f_is_active}
                                onChange={e => setForm(f => ({ ...f, f_is_active: e.target.checked }))} />
                            <Typography variant="body2">Active</Typography>
                        </Box>
                    )}

                    {/* Images — only after the product exists */}
                    {editTarget ? (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Photos · folder <code>{editTarget.f_image_folder}</code>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                                {(editTarget.images || []).map(url => (
                                    <Box key={url} sx={{ position: 'relative', width: 64, height: 64 }}>
                                        <Box component="img" src={url} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }} />
                                        <IconButton size="small" onClick={() => handleDeleteImage(url)} disabled={imgBusy}
                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, p: 0.25, '&:hover': { bgcolor: 'error.50' } }}>
                                            <DeleteIcon sx={{ fontSize: 14 }} color="error" />
                                        </IconButton>
                                    </Box>
                                ))}
                                <Box component="label" sx={{ width: 64, height: 64, borderRadius: 1.5, border: '1.5px dashed', borderColor: 'grey.400', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: '#fafafa' }}>
                                    {imgBusy ? <CircularProgress size={18} /> : <PhotoCameraIcon sx={{ color: 'text.disabled' }} />}
                                    <input type="file" accept="image/*" hidden onChange={handleUploadImage} />
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="caption" color="text.secondary">Save the product first, then add photos.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
                    <Button variant="contained" onClick={saveProduct} disabled={saving} sx={{ textTransform: 'none' }}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : (editTarget ? 'Save' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Category form dialog ── */}
            <Dialog open={catFormOpen} onClose={() => setCatFormOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {catEditTarget ? 'Edit Category' : 'New Category'}
                    <IconButton size="small" onClick={() => setCatFormOpen(false)}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
                    <TextField size="small" label="Name" value={catForm.f_name}
                        onChange={e => setCatForm(f => ({ ...f, f_name: e.target.value }))} />
                    <TextField size="small" label="Description" multiline minRows={2} value={catForm.f_description}
                        onChange={e => setCatForm(f => ({ ...f, f_description: e.target.value }))} />
                    {catEditTarget && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch size="small" checked={catForm.f_is_active}
                                onChange={e => setCatForm(f => ({ ...f, f_is_active: e.target.checked }))} />
                            <Typography variant="body2">Active</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setCatFormOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
                    <Button variant="contained" onClick={saveCategory} disabled={catSaving} sx={{ textTransform: 'none' }}>
                        {catSaving ? <CircularProgress size={18} color="inherit" /> : (catEditTarget ? 'Save' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const CommunitiesDialog = ({ open, onClose, onSnack }) => {
    const [tab, setTab] = useState(0);
    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    // Link Areas tab
    const [areas, setAreas] = useState([]);
    const [areasLoading, setAreasLoading] = useState(false);
    const [areaSearch, setAreaSearch] = useState('');
    const [linkingId, setLinkingId] = useState(null);
    const svcRef = useRef(null);
    const placeSvcRef = useRef(null);
    const [placeInput, setPlaceInput] = useState('');
    const [placeSuggestions, setPlaceSuggestions] = useState([]);
    const [placesLoading, setPlacesLoading] = useState(false);

    const fetchCommunities = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getCommunities();
            setCommunities(res.data.communities);
        } catch {
            onSnack('Failed to load communities', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchAreas = async () => {
        setAreasLoading(true);
        try {
            const res = await adminAPI.getBusinessAreas();
            setAreas(res.data.areas);
        } catch {
            onSnack('Failed to load areas', 'error');
        } finally {
            setAreasLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        fetchCommunities();
        fetchAreas();
    }, [open]);

    const handleLinkArea = async (areaId, communityId) => {
        setLinkingId(areaId);
        try {
            await adminAPI.linkAreaToCommunity(areaId, communityId || null);
            const community = communities.find(c => c.f_id === communityId);
            setAreas(prev => prev.map(a =>
                a.f_id === areaId
                    ? { ...a, f_community_id: communityId || null, community_name: community?.f_name || null }
                    : a
            ));
        } catch {
            onSnack('Failed to link area', 'error');
        } finally {
            setLinkingId(null);
        }
    };

    // Load Google Maps script once
    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_PUBLIC_KEY;
        if (!apiKey) return;
        const scriptId = 'google-maps-places-script';
        if (document.getElementById(scriptId)) return;
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        document.head.appendChild(script);
    }, []);

    // Init AutocompleteService when form opens
    useEffect(() => {
        if (!formOpen || editTarget) return;
        const init = () => {
            if (!window.google?.maps?.places?.AutocompleteService) { setTimeout(init, 200); return; }
            svcRef.current = new window.google.maps.places.AutocompleteService();
            const mapDiv = document.createElement('div');
            placeSvcRef.current = new window.google.maps.places.PlacesService(mapDiv);
        };
        setTimeout(init, 100);
    }, [formOpen, editTarget]);

    const handlePlaceInputChange = (value) => {
        setPlaceInput(value);
        if (!value || value.length < 2 || !svcRef.current) { setPlaceSuggestions([]); return; }
        setPlacesLoading(true);
        svcRef.current.getPlacePredictions(
            { input: value, componentRestrictions: { country: 'in' }, types: ['establishment', 'geocode'] },
            (predictions, status) => {
                setPlacesLoading(false);
                setPlaceSuggestions(
                    status === window.google.maps.places.PlacesServiceStatus.OK ? predictions : []
                );
            }
        );
    };

    const handlePlaceSelect = (prediction) => {
        if (!prediction?.place_id || !placeSvcRef.current) return;
        placeSvcRef.current.getDetails(
            { placeId: prediction.place_id, fields: ['name', 'geometry', 'address_components', 'place_id'] },
            (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
                const get = (type) =>
                    (place.address_components || []).find(c => c.types.includes(type))?.long_name || '';
                setForm({
                    f_name: place.name || '',
                    f_city: get('locality') || get('administrative_area_level_2') || '',
                    f_state: get('administrative_area_level_1') || '',
                    f_pincode: get('postal_code') || '',
                    f_latitude: place.geometry?.location?.lat() || '',
                    f_longitude: place.geometry?.location?.lng() || '',
                    f_google_place_id: place.place_id || '',
                });
            }
        );
    };

    const openAdd = () => {
        setEditTarget(null);
        setForm(EMPTY_FORM);
        setPlaceInput('');
        setPlaceSuggestions([]);
        setFormOpen(true);
    };

    const openEdit = (c) => {
        setEditTarget(c);
        setForm({
            f_name: c.f_name,
            f_city: c.f_city,
            f_state: c.f_state,
            f_pincode: c.f_pincode || '',
            f_latitude: c.f_latitude || '',
            f_longitude: c.f_longitude || '',
            f_google_place_id: c.f_google_place_id || '',
        });
        setFormOpen(true);
    };

    const handleSave = async () => {
        if (!form.f_name || !form.f_city || !form.f_state) {
            onSnack('Name, city and state are required', 'error');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                f_latitude: form.f_latitude !== '' ? parseFloat(form.f_latitude) : null,
                f_longitude: form.f_longitude !== '' ? parseFloat(form.f_longitude) : null,
            };
            if (editTarget) {
                await adminAPI.updateCommunity(editTarget.f_id, payload);
                onSnack('Community updated');
            } else {
                await adminAPI.createCommunity(payload);
                onSnack('Community added');
            }
            setFormOpen(false);
            fetchCommunities();
        } catch (err) {
            onSnack(err.response?.data?.detail || 'Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (c) => {
        try {
            await adminAPI.updateCommunity(c.f_id, { f_is_active: !c.f_is_active });
            setCommunities(prev => prev.map(x => x.f_id === c.f_id ? { ...x, f_is_active: !c.f_is_active } : x));
        } catch {
            onSnack('Failed to update', 'error');
        }
    };

    const handleDelete = async (c) => {
        if (!window.confirm(`Delete "${c.f_name}"?`)) return;
        setDeletingId(c.f_id);
        try {
            await adminAPI.deleteCommunity(c.f_id);
            setCommunities(prev => prev.filter(x => x.f_id !== c.f_id));
            onSnack('Community deleted');
        } catch {
            onSnack('Failed to delete', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const filtered = communities.filter(c => {
        if (!search) return true;
        const q = search.toLowerCase();
        return c.f_name.toLowerCase().includes(q) || c.f_city.toLowerCase().includes(q) || c.f_state.toLowerCase().includes(q);
    });

    return (
        <>
            <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
                <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        Communities
                    </Typography>
                    {tab === 0 && (
                        <Button size="small" variant="contained" color="inherit" startIcon={<AddIcon />} onClick={openAdd}
                            sx={{ color: 'primary.main', bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}>
                            Add
                        </Button>
                    )}
                    <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
                </DialogTitle>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={`Master List (${filtered.length})`} />
                    <Tab label={`Link Areas (${areas.filter(a => !a.f_community_id).length} unlinked)`} />
                </Tabs>

                <DialogContent sx={{ px: 1, pt: 1 }}>
                    {/* ── Tab 0: Communities master list ── */}
                    {tab === 0 && (
                        <>
                            <TextField
                                size="small" fullWidth
                                placeholder="Search by name, city or state..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                sx={{ mb: 1, px: 1 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            />
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                            ) : (
                                <TableContainer component={Paper} elevation={1}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>City</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Pincode</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Active</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filtered.map(c => (
                                                <TableRow key={c.f_id} hover>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.f_name}</TableCell>
                                                    <TableCell>{c.f_city}</TableCell>
                                                    <TableCell>{c.f_state}</TableCell>
                                                    <TableCell>{c.f_pincode || '—'}</TableCell>
                                                    <TableCell align="center">
                                                        <Switch size="small" checked={c.f_is_active} onChange={() => handleToggleActive(c)} color="success" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => openEdit(c)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <span>
                                                                <IconButton size="small" color="error" disabled={deletingId === c.f_id} onClick={() => handleDelete(c)}>
                                                                    {deletingId === c.f_id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {filtered.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                        <Typography color="text.secondary">No communities found</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}

                    {/* ── Tab 1: Link Areas ── */}
                    {tab === 1 && (
                        <>
                            <TextField
                                size="small" fullWidth
                                placeholder="Search by area or business name..."
                                value={areaSearch}
                                onChange={e => setAreaSearch(e.target.value)}
                                sx={{ mb: 1, px: 1 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            />
                            {areasLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                            ) : (
                                <TableContainer component={Paper} elevation={1}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Business</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Linked Community</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {areas
                                                .filter(a => {
                                                    if (!areaSearch) return true;
                                                    const q = areaSearch.toLowerCase();
                                                    return a.f_name.toLowerCase().includes(q) || (a.business_name || '').toLowerCase().includes(q);
                                                })
                                                .map(a => (
                                                    <TableRow key={a.f_id} hover sx={{ opacity: linkingId === a.f_id ? 0.5 : 1 }}>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            <Typography variant="body2">{a.business_name}</Typography>
                                                            {a.business_city && <Typography variant="caption" color="text.secondary">{a.business_city}</Typography>}
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            {a.f_name}
                                                            {!a.f_community_id && (
                                                                <Chip label="unlinked" size="small" color="warning" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem' }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <FormControl size="small" fullWidth disabled={linkingId === a.f_id}>
                                                                <Select
                                                                    value={a.f_community_id || ''}
                                                                    displayEmpty
                                                                    onChange={e => handleLinkArea(a.f_id, e.target.value || null)}
                                                                >
                                                                    <MenuItem value=""><em>— Not linked —</em></MenuItem>
                                                                    {communities.filter(c => c.f_is_active).map(c => (
                                                                        <MenuItem key={c.f_id} value={c.f_id}>
                                                                            {c.f_name} · {c.f_city}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add / Edit form dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {editTarget ? 'Edit Community' : 'Add Community'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    {!editTarget && (
                        <Autocomplete
                            freeSolo
                            options={placeSuggestions}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.description)}
                            loading={placesLoading}
                            inputValue={placeInput}
                            onInputChange={(_, val) => handlePlaceInputChange(val)}
                            onChange={(_, option) => { if (option && option.place_id) handlePlaceSelect(option); }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Search on Google Maps"
                                    placeholder="Type community name..."
                                    helperText="Select a suggestion to auto-fill fields below"
                                />
                            )}
                        />
                    )}
                    <TextField size="small" fullWidth label="Community Name *" value={form.f_name} onChange={e => setForm(f => ({ ...f, f_name: e.target.value }))} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField size="small" fullWidth label="City *" value={form.f_city} onChange={e => setForm(f => ({ ...f, f_city: e.target.value }))} />
                        <TextField size="small" fullWidth label="State *" value={form.f_state} onChange={e => setForm(f => ({ ...f, f_state: e.target.value }))} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField size="small" fullWidth label="Pincode" value={form.f_pincode} onChange={e => setForm(f => ({ ...f, f_pincode: e.target.value }))} />
                        <TextField size="small" fullWidth label="Latitude" value={form.f_latitude} onChange={e => setForm(f => ({ ...f, f_latitude: e.target.value }))} />
                        <TextField size="small" fullWidth label="Longitude" value={form.f_longitude} onChange={e => setForm(f => ({ ...f, f_longitude: e.target.value }))} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : editTarget ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SuperAdminDashboard;
